declare const __AUTH_SERVER_BASE_URL__: string;

declare global {
    interface Window {
        AuthUiClient: {
            Auth: typeof AuthUiClient.Auth;
            AuthError: typeof AuthUiClient.AuthError;
            AuthErrorFactory: typeof AuthUiClient.AuthErrorFactory;
            PlatformJwt: typeof AuthUiClient.PlatformJwt;
            [key: string]: unknown;
        };
        auth: unknown;
    }
}

const isAuthlibLoaded = (): boolean => {
    return typeof window !== 'undefined' && window.AuthUiClient?.Auth !== undefined;
};

export const initializeAuthlib = (): void => {
    if (typeof window === 'undefined') return;
    if (getAuthFromWindow() !== null) return;
    if (!isAuthlibLoaded()) return;

    const auth = window.AuthUiClient.Auth.getInstance({
        baseUrl: __AUTH_SERVER_BASE_URL__,
        defaultRedirectUrl: window.location.href,
        system: 'platform',
        autoRenewToken: true,
        logLevel: 'debug',
    });

    window.auth = auth;
};

export const checkAndRestoreSession = async (): Promise<boolean> => {
    const auth = getAuthFromWindow();

    if (!auth) {
        console.log('No auth found');
        return false;
    }

    try {
        const token = await auth.getToken();
        return Boolean(token);
    } catch (error) {
        console.error('Session restore error:', error);
        return false;
    }
};

export const getErrorMessage = (
    error: unknown,
    fallback = 'An unexpected error occurred',
): string => {
    if (error instanceof window.AuthUiClient.AuthError) {
        return error.getUserMessage();
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return fallback;
};

export const getAuthFromWindow = (): AuthUiClient.Auth | null => {
    if (typeof window === 'undefined' || !window.auth) {
        return null;
    }

    return window.auth as AuthUiClient.Auth;
};

export const getAccessToken = async (): Promise<string | undefined> => {
    try {
        const auth = getAuthFromWindow();
        return auth ? await auth.getToken() : undefined;
    } catch (err) {
        console.error(getErrorMessage(err));
        return undefined;
    }
};

export const getAppId = (): string | undefined => {
    return (window as Window & { __appId?: string }).__appId || undefined;
};

export const apiFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const token = await getAccessToken();
    const appId = getAppId();

    return fetch(url, {
        ...init,
        headers: {
            ...init.headers,
            Authorization: `Bearer ${token}`,
            ...(appId ? { 'Boomi-Fabric-App-id': appId } : {}),
        },
    });
};

export const signOut = async (): Promise<void> => {
    try {
        const auth = getAuthFromWindow();
        auth && (await auth.logout());
    } catch (err) {
        console.error(getErrorMessage(err));
    }
};
